/**
 * Helpers para la página /updates: clasificación de versión, filtro y orden
 * de la lista de agentes. Funciones puras — testeables sin DOM.
 */

import type { Agent } from '@/types/agent';
import { compareVersions } from '@/lib/notifications/buildNotifications';

export type AgentVersionStatus = 'up-to-date' | 'outdated' | 'unknown' | 'offline';

export function agentVersionStatus(agent: Agent, latestVersion?: string): AgentVersionStatus {
  if (!agent.connected) return 'offline';
  if (!latestVersion || !agent.version) return 'unknown';
  return compareVersions(agent.version, latestVersion) >= 0 ? 'up-to-date' : 'outdated';
}

export type StatusFilter = 'all' | 'needs-update' | 'up-to-date' | 'offline' | 'unknown';

export type SortKey = 'hostname' | 'versionStatus' | 'os' | 'version' | 'lastSeen';
export type SortOrder = 'asc' | 'desc';

// Orden estable para ordenar por estado de versión: outdated primero (lo que
// el usuario quiere encontrar más rápido), después up-to-date, unknown, offline.
const VERSION_STATUS_RANK: Record<AgentVersionStatus, number> = {
  outdated: 0,
  'up-to-date': 1,
  unknown: 2,
  offline: 3,
};

export interface FilterSortOptions {
  search?: string;
  statusFilter?: StatusFilter;
  sortBy?: SortKey;
  sortOrder?: SortOrder;
  /** Versión del manifiesto. Si no se pasa, no se puede clasificar outdated/up-to-date. */
  latestVersion?: string;
}

/**
 * Aplica filtro por estado de versión, búsqueda libre (hostname/agentId) y
 * orden por columna a la lista de agentes. Devuelve SIEMPRE una lista nueva
 * (no muta el input).
 *
 * El orden es estable: cuando dos agentes empatan en el sortBy, caen al
 * hostname como tiebreak para que la tabla no "baile" entre renders.
 */
export function filterAndSortAgents(agents: Agent[], options: FilterSortOptions = {}): Agent[] {
  const {
    search = '',
    statusFilter = 'all',
    sortBy = 'hostname',
    sortOrder = 'asc',
    latestVersion,
  } = options;

  const q = search.trim().toLowerCase();

  let list = agents.filter((agent) => {
    const versionSt = agentVersionStatus(agent, latestVersion);

    // Filtro por estado
    if (statusFilter === 'needs-update' && versionSt !== 'outdated') return false;
    if (statusFilter === 'up-to-date' && versionSt !== 'up-to-date') return false;
    if (statusFilter === 'offline' && versionSt !== 'offline') return false;
    if (statusFilter === 'unknown' && versionSt !== 'unknown') return false;

    // Búsqueda libre sobre hostname, agentId, OS y versión actual.
    // Construimos el haystack concatenando los campos relevantes en minúsculas.
    if (q) {
      const haystack =
        `${agent.hostname ?? ''} ${agent.agentId} ${agent.os ?? ''} ${agent.version ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }

    return true;
  });

  list = [...list].sort((a, b) => {
    const va = agentVersionStatus(a, latestVersion);
    const vb = agentVersionStatus(b, latestVersion);
    let cmp = 0;
    switch (sortBy) {
      case 'hostname':
        cmp = (a.hostname ?? a.agentId).localeCompare(b.hostname ?? b.agentId);
        break;
      case 'versionStatus':
        cmp = VERSION_STATUS_RANK[va] - VERSION_STATUS_RANK[vb];
        break;
      case 'os':
        cmp = (a.os ?? '').localeCompare(b.os ?? '');
        break;
      case 'version':
        cmp = (a.version ?? '').localeCompare(b.version ?? '', undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        break;
      case 'lastSeen':
        cmp = (a.lastSeen ?? '').localeCompare(b.lastSeen ?? '');
        break;
    }
    if (cmp === 0) {
      // Tiebreak estable: hostname, para que el orden no baile entre renders.
      cmp = (a.hostname ?? a.agentId).localeCompare(b.hostname ?? b.agentId);
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  return list;
}
